import { useLocalStorageWithSubscription } from "@sundae/react-hooks";
import { useWalletDetails } from "@sundae/wallet-react";
import { noop } from "lodash";
import { Context, FC, PropsWithChildren, createContext, useContext, useMemo } from "react";
import { LOCAL_STORAGE_PENDING_YF_TRANSACTIONS } from "../../constants/localStorage.constants";
import {
  useYieldFarmingEarningsQuery,
  useYieldFarmingEstimatedEarningsQuery,
  useYieldFarmingPositionsQuery,
  useYieldFarmingProgramsQuery,
} from "../../gql/hooks/farms.query";
import { TPendingYieldFarmingTransaction } from "../../hooks/farms/useYieldFarmingPositions";
import {
  IYieldFarmingAction,
  IYieldFarmingContext,
  IYieldFarmingState,
  TYieldFarmingRefetch,
} from "./types";

export const defaultYieldFarmingState: IYieldFarmingState = {
  data: {
    estimates: [],
    earnings: [],
    pendingYieldFarmingTransactions: [],
    position: [],
    programs: [],
  },
  handlers: {
    setPendingYieldFarmingTransactions: noop,
  },
  refetch: {
    estimates: noop as TYieldFarmingRefetch,
    earnings: noop as TYieldFarmingRefetch,
    position: noop as TYieldFarmingRefetch,
    programs: noop as TYieldFarmingRefetch,
  },
  isFetching: false,
  isFetched: false,
  isLoading: false,
};

const YieldFarmingContext: Context<IYieldFarmingContext> = createContext({
  state: defaultYieldFarmingState,
  dispatch: (_value: IYieldFarmingAction) => {},
});

export const YieldFarmingContextProvider: FC<PropsWithChildren> = ({ children }) => {
  /* -------------------------------------------------------------------------------------------------
   * Local Storage State for Pending Transactions
   * -----------------------------------------------------------------------------------------------*/
  const [pendingYieldFarmingTransactions, setPendingYieldFarmingTransactions] =
    useLocalStorageWithSubscription(
      LOCAL_STORAGE_PENDING_YF_TRANSACTIONS,
      [] as TPendingYieldFarmingTransaction[]
    );
  const {
    details: { mainAddress, stakingAddress },
  } = useWalletDetails();
  const beneficiary = [stakingAddress, mainAddress].sort();
  const {
    data: dataYieldFarmingPositionsQuery,
    isFetched: isFetchedYieldFarmingPositionsQuery,
    isFetching: isFetchingYieldFarmingPositionsQuery,
    isLoading: isLoadingYieldFarmingPositionsQuery,
    error: errorYieldFarmingPositionsQuery,
    refetch: refetchYieldFarmingPositionsQuery,
  } = useYieldFarmingPositionsQuery({ beneficiary });
  const {
    data: dataYieldFarmingProgramsQuery,
    isFetched: isFetchedYieldFarmingProgramsQuery,
    isFetching: isFetchingYieldFarmingProgramsQuery,
    isLoading: isLoadingYieldFarmingProgramsQuery,
    error: errorYieldFarmingProgramsQuery,
    refetch: refetchYieldFarmingProgramsQuery,
  } = useYieldFarmingProgramsQuery();
  const {
    data: dataYieldFarmingEarningsQuery,
    isFetched: isFetchedYieldFarmingEarningsQuery,
    isFetching: isFetchingYieldFarmingEarningsQuery,
    isLoading: isLoadingYieldFarmingEarningsQuery,
    error: errorYieldFarmingEarningsQuery,
    refetch: refetchYieldFarmingEarningsQuery,
  } = useYieldFarmingEarningsQuery(beneficiary);
  const {
    data: estimates,
    error: errorEstimates,
    isFetching: isFetchingEstimates,
    isLoading: isLoadingEstimates,
    isFetched: isFetchedEstimates,
    refetch: refetchEstimates,
  } = useYieldFarmingEstimatedEarningsQuery(beneficiary);

  const { isFetching, isFetched, isLoading } = useMemo(() => {
    if (
      isFetchingYieldFarmingPositionsQuery ||
      isFetchingYieldFarmingProgramsQuery ||
      isFetchingYieldFarmingEarningsQuery ||
      isFetchingEstimates
    ) {
      if (
        isLoadingEstimates ||
        isLoadingYieldFarmingEarningsQuery ||
        isLoadingYieldFarmingPositionsQuery ||
        isLoadingYieldFarmingProgramsQuery
      ) {
        return { isFetching: true, isLoading: true, isFetched: false };
      }

      return { isFetching: true, isLoading: false, isFetched: false };
    }

    if (
      isLoadingEstimates ||
      isLoadingYieldFarmingEarningsQuery ||
      isLoadingYieldFarmingPositionsQuery ||
      isLoadingYieldFarmingProgramsQuery
    ) {
      return { isFetching: false, isLoading: true, isFetched: false };
    }

    if (
      isFetchedYieldFarmingPositionsQuery &&
      isFetchedYieldFarmingProgramsQuery &&
      isFetchedYieldFarmingEarningsQuery &&
      isFetchedEstimates
    ) {
      return { isFetched: true, isLoading: false, isFetching: false };
    }
    return { isFetched: false, isFetching: false, isLoading: false };
  }, [
    isFetchedEstimates,
    isFetchedYieldFarmingEarningsQuery,
    isFetchedYieldFarmingPositionsQuery,
    isFetchedYieldFarmingProgramsQuery,
    isFetchingEstimates,
    isFetchingYieldFarmingEarningsQuery,
    isFetchingYieldFarmingPositionsQuery,
    isFetchingYieldFarmingProgramsQuery,
    isLoadingEstimates,
    isLoadingYieldFarmingEarningsQuery,
    isLoadingYieldFarmingPositionsQuery,
    isLoadingYieldFarmingProgramsQuery,
  ]);

  return (
    <YieldFarmingContext.Provider
      value={{
        state: {
          data: {
            estimates: estimates || [],
            earnings: dataYieldFarmingEarningsQuery || [],
            pendingYieldFarmingTransactions,
            position: dataYieldFarmingPositionsQuery || [],
            programs: dataYieldFarmingProgramsQuery || [],
          },
          error: {
            estimatesQuery: errorEstimates as Error,
            positionsQuery: errorYieldFarmingPositionsQuery as Error,
            programsQuery: errorYieldFarmingProgramsQuery as Error,
            earningsQuery: errorYieldFarmingEarningsQuery as Error,
          },
          handlers: { setPendingYieldFarmingTransactions },
          refetch: {
            position: refetchYieldFarmingPositionsQuery,
            programs: refetchYieldFarmingProgramsQuery,
            earnings: refetchYieldFarmingEarningsQuery,
            estimates: refetchEstimates,
          },
          isFetched,
          isFetching,
          isLoading,
        },
        dispatch: (_value: IYieldFarmingAction) => {},
      }}
    >
      {children}
    </YieldFarmingContext.Provider>
  );
};

// Utility hook.
export const useYieldFarmingContext = (): IYieldFarmingContext => useContext(YieldFarmingContext);
